import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { Clock, Users, Trophy, ChevronRight, Plus, Trash } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Tournament from '../models/Tournament';
import Team from '../models/Team';

const TournamentSetupPage = () => {
  const navigate = useNavigate();
  const { setupTournament } = useAppContext();
  
  // Form states
  const [name, setName] = useState('Soccer Tournament');
  const [format, setFormat] = useState('5v5');
  const [eventDuration, setEventDuration] = useState(120); // minutes
  const [matchDuration, setMatchDuration] = useState(10); // minutes
  const [breakDuration, setBreakDuration] = useState(2); // minutes
  const [numberOfFields, setNumberOfFields] = useState(1);
  const [fields, setFields] = useState([{id: 1, name: 'Field 1'}]);
  const [hasFinals, setHasFinals] = useState(true);
  const [teams, setTeams] = useState([
    new Team({ color: '#FF4136' }), // Red Team
    new Team({ color: '#0074D9' })  // Blue Team
  ]);
  
  // Add a new team
  const addTeam = () => {
    // Generate a random color
    const colors = ['#FF4136', '#0074D9', '#2ECC40', '#FFDC00', '#FF851B', '#B10DC9', '#F012BE', '#7FDBFF', '#001f3f', '#39CCCC'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    
    // Create a new team with just the color - name will default based on color
    setTeams([...teams, new Team({ color: randomColor })]);
  };
  
  // Remove a team
  const removeTeam = (teamId) => {
    setTeams(teams.filter(team => team.id !== teamId));
  };
  
  // Update team data
  const updateTeam = (index, data) => {
    const updatedTeams = [...teams];
    updatedTeams[index].update(data);
    setTeams(updatedTeams);
  };
  
  // Update fields based on number
  useEffect(() => {
    const newFields = [];
    for (let i = 1; i <= numberOfFields; i++) {
      const existingField = fields.find(f => f.id === i);
      if (existingField) {
        newFields.push(existingField);
      } else {
        newFields.push({id: i, name: `Field ${i}`});
      }
    }
    setFields(newFields);
  }, [numberOfFields]);

  // Update field name
  const updateFieldName = (index, name) => {
    const updatedFields = [...fields];
    updatedFields[index].name = name;
    setFields(updatedFields);
  };

  // Create the tournament
  const createTournament = () => {
    if (teams.length < 2) {
      alert('You need at least 2 teams to create a tournament');
      return;
    }
    
    const tournament = new Tournament({
      name,
      format,
      eventDuration,
      matchDuration,
      breakDuration,
      numberOfFields,
      fields,
      hasFinals
    });
    
    setupTournament(tournament, teams);
    navigate('/schedule');
  };
  
  return (
    <div className="px-4 py-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <Trophy size={24} className="mr-2" />
        Tournament Setup
      </h1>
      
      <Card className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Tournament Details</h2>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Tournament Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium block mb-1">Game Format</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                className={`px-3 py-2 rounded-lg border ${format === '5v5' ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300 dark:border-gray-700'}`}
                onClick={() => setFormat('5v5')}
              >
                5v5
              </button>
              <button
                className={`px-3 py-2 rounded-lg border ${format === '7v7' ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300 dark:border-gray-700'}`}
                onClick={() => setFormat('7v7')}
              >
                7v7
              </button>
              <button
                className={`px-3 py-2 rounded-lg border ${format === '11v11' ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300 dark:border-gray-700'}`}
                onClick={() => setFormat('11v11')}
              >
                11v11
              </button>
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium block mb-1 flex items-center">
              <Clock size={16} className="mr-1" />
              Event Duration (minutes)
            </label>
            <select
              value={eventDuration}
              onChange={(e) => setEventDuration(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
            >
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
              <option value={180}>3 hours</option>
              <option value={240}>4 hours</option>
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium block mb-1 flex items-center">
              <Clock size={16} className="mr-1" />
              Match Duration (minutes)
            </label>
            <select
              value={matchDuration}
              onChange={(e) => setMatchDuration(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
            >
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
              <option value={15}>15 minutes</option>
              <option value={20}>20 minutes</option>
              <option value={30}>30 minutes</option>
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium block mb-1 flex items-center">
              <Clock size={16} className="mr-1" />
              Break Between Matches (minutes)
            </label>
            <select
              value={breakDuration}
              onChange={(e) => setBreakDuration(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
            >
              <option value={0}>No break</option>
              <option value={1}>1 minute</option>
              <option value={2}>2 minutes</option>
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium block mb-1 flex items-center">
              Number of Fields
            </label>
            <select
              value={numberOfFields}
              onChange={(e) => setNumberOfFields(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
            >
              {[1, 2, 3, 4, 5, 6].map(num => (
                <option key={num} value={num}>{num} field{num > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
          
          {numberOfFields > 1 && (
            <div>
              <label className="text-sm font-medium block mb-1">Field Names</label>
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center">
                    <input
                      type="text"
                      value={field.name}
                      onChange={(e) => updateFieldName(index, e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                      placeholder={`Field ${field.id}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex items-center">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={hasFinals}
                onChange={(e) => setHasFinals(e.target.checked)}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-gray-700"
              />
              <span className="ml-2">Include finals rounds</span>
            </label>
          </div>
        </div>
      </Card>
      
      <Card className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold flex items-center">
            <Users size={20} className="mr-1" />
            Teams ({teams.length})
          </h2>
          <Button small icon={<Plus size={16} />} onClick={addTeam}>
            Add Team
          </Button>
        </div>
        
        <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
          {teams.map((team, index) => (
            <div
              key={team.id}
              className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-between"
            >
              <div className="flex items-center flex-1 mr-2">
                <div 
                  className="w-5 h-5 rounded-full mr-2 cursor-pointer relative" 
                  style={{ backgroundColor: team.color }}
                  onClick={() => {
                    // For mobile compatibility, we'll use a select with preset colors
                    const colors = [
                      '#FF4136', // Red
                      '#FF851B', // Orange
                      '#FFDC00', // Yellow
                      '#2ECC40', // Green
                      '#0074D9', // Blue
                      '#B10DC9', // Purple
                      '#F012BE', // Pink
                      '#111111', // Black
                      '#AAAAAA', // Gray
                      '#FFFFFF'  // White
                    ];
                    
                    // Create color selection modal
                    const colorPickerDiv = document.createElement('div');
                    colorPickerDiv.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50';
                    colorPickerDiv.innerHTML = `
                      <div class="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg max-w-xs w-full">
                        <h3 class="text-lg font-semibold mb-3">Select Team Color</h3>
                        <div class="grid grid-cols-5 gap-2 mb-4">
                          ${colors.map(color => 
                            `<button class="w-10 h-10 rounded-full" style="background-color: ${color}" 
                            data-color="${color}"></button>`
                          ).join('')}
                        </div>
                        <button class="w-full py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg">Cancel</button>
                      </div>
                    `;
                    
                    // Add to body
                    document.body.appendChild(colorPickerDiv);
                    
                    // Add event listeners
                    const colorButtons = colorPickerDiv.querySelectorAll('[data-color]');
                    colorButtons.forEach(btn => {
                      btn.addEventListener('click', () => {
                        const newColor = btn.getAttribute('data-color');
                        updateTeam(index, { color: newColor });
                        document.body.removeChild(colorPickerDiv);
                      });
                    });
                    
                    // Close on cancel
                    const cancelBtn = colorPickerDiv.querySelector('button:last-child');
                    cancelBtn.addEventListener('click', () => {
                      document.body.removeChild(colorPickerDiv);
                    });
                    
                    // Close on background click
                    colorPickerDiv.addEventListener('click', (e) => {
                      if (e.target === colorPickerDiv) {
                        document.body.removeChild(colorPickerDiv);
                      }
                    });
                  }}
                />
                <input 
                  type="text"
                  value={team.name}
                  onChange={(e) => updateTeam(index, { name: e.target.value })}
                  className="flex-1 bg-transparent border-b border-gray-200 dark:border-gray-700 focus:outline-none focus:border-blue-500"
                />
              </div>
              <button 
                onClick={() => removeTeam(team.id)}
                className="text-red-500 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900"
                disabled={teams.length <= 2}
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
        
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          {teams.length < 2 && "Add at least 2 teams to create a tournament"}
        </div>
      </Card>
      
      <Button 
        primary 
        className="w-full h-12 mt-4" 
        onClick={createTournament} 
        disabled={teams.length < 2}
        icon={<ChevronRight size={20} />}
      >
        Create Tournament
      </Button>
    </div>
  );
};

export default TournamentSetupPage;
